
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 5,
  description: "Link's update mode allows you to fix Items requiring re-authentication, or for users to grant additional permissions on existing Items."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Updating Items via Link' subtitle='Use update mode to add permissions to Items, or to resolve ITEM_LOGIN_REQUIRED status' mdxType="Header" />
    <Image src='/assets/img/docs/update-mode.png' alt="Plaid Link update mode (with account selection enabled): Connect account, enter credentials, select accounts, and see success confirmation screen." width='624' expandable={true} mdxType="Image" />
    <h4 {...{
      "id": "when-to-use-update-mode"
    }}>{`When to use update mode`}</h4>
    <p>{`Update mode refers to any Link session launched for an Item after that Item's initial creation. Update mode is used when an existing Item requires input from a user, such as to update credentials or to grant additional consent.`}</p>
    <p>{`One common use of update mode is to update authentication or authorization for an Item. This can be required when access to an existing Item stops working: for example, if the end user changes a password on an Item that does not use an OAuth connection, or if authorization for the Item has expired. Update mode can also be used pre-emptively to renew authorization for an Item.`}</p>
    <p>{`Update mode can also be used to request permissions that the user did not originally grant during the initial Link flow. This can include specific OAuth permissions, access to new accounts, or additional product scopes or use cases. Update mode can be used to manage `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#scoped-consent"
      }}>{`permissions granted via OAuth`}</a>{`, as well as permissions granted directly through Plaid via `}<a parentName="p" {...{
        "href": "/docs/link/customization/#account-select"
      }}>{`Account Select`}</a>{` or `}<a parentName="p" {...{
        "href": "/docs/link/data-transparency-messaging-migration-guide/"
      }}>{`Data Transparency Messaging`}</a>{`. When used to request additional permissions, Plaid will show a streamlined Link experience in update mode. For example, if a user is being asked to add a new product, they may not need to log in to their financial institution.`}</p>
    <Callout mdxType="Callout">
      <p>{`For some older Items created before March 2023, data for all accounts may be available, even if the user did not select the account in Account Select, and access to new accounts may be granted without needing to use the update mode flow. If these Items are at OAuth institutions, they will be updated to use the current Account Select behavior when they are sent through update mode. For more details, see `}<a parentName="p" {...{
          "href": "/docs/link/customization/#account-select"
        }}>{`Account Select`}</a>{`.`}</p>
    </Callout>
    <p>{`Update mode is also used to launch feature-specific flows that require returning to Link. For example, update mode is used for confirming the micro-deposit verification code in the `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day/"
      }}>{`Same-Day Micro-deposits`}</a>{` Auth flow or to collect statement copies when using `}<a parentName="p" {...{
        "href": "/docs/identity/identity-document-upload"
      }}>{`Identity Document Upload`}</a>{`. For details on using update mode with these flows, see the product-specific pages. `}</p>
    <h5 {...{
      "id": "resolving-item_login_required-pending_expiration-or-pending_disconnect"
    }}>{`Resolving ITEM_LOGIN_REQUIRED, PENDING_EXPIRATION, or PENDING_DISCONNECT`}</h5>
    <p>{`Receiving an `}<a parentName="p" {...{
        "href": "/docs/errors/item#item_login_required"
      }}><inlineCode parentName="a">{`ITEM_LOGIN_REQUIRED`}</inlineCode></a>{` error or a `}<a parentName="p" {...{
        "href": "/docs/api/items/#pending_expiration"
      }}><inlineCode parentName="a">{`PENDING_EXPIRATION`}</inlineCode></a>{` or `}<a parentName="p" {...{
        "href": "/docs/api/items/#pending_disconnect"
      }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode></a>{` webhook indicates that the Item should be re-initialized via update mode.`}</p>
    <p>{`If you receive the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` error after calling a Plaid endpoint, implement Link in update mode during the user flow, and ask the user to re-authenticate before proceeding to the next step in your flow.`}</p>
    <p>{`If you receive the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` error via the `}<a parentName="p" {...{
        "href": "/docs/api/items/#error"
      }}><inlineCode parentName="a">{`ITEM: ERROR`}</inlineCode></a>{` webhook, or if you receive either the `}<a parentName="p" {...{
        "href": "/docs/api/items/#pending_expiration"
      }}><inlineCode parentName="a">{`PENDING_EXPIRATION`}</inlineCode></a>{` or `}<a parentName="p" {...{
        "href": "/docs/api/items/#pending_disconnect"
      }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode></a>{` webhook, re-authenticate with Link in update mode when the user is next in your app. You will need to tell your user (using in-app messaging and/or notifications such as email or text message) to return to your app to fix their Item.`}</p>
    <p>{`When resolving these issues, for most institutions, Plaid will present an abbreviated re-authentication flow requesting only the minimum user input required to repair the Item. For example, if the Item entered an error state because the user's OTP token expired, the user may be prompted to provide another OTP token, but not to fully re-login to the institution.`}</p>
    <Callout title="Self-healing Item notifications" mdxType="Callout">
      <p>{`If a user has connected the same account via Plaid to multiple different apps, resolving the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` error for an Item in one app may also fix Items in other apps. If one of your Items is fixed in this way, Plaid will notify you via the `}<a parentName="p" {...{
          "href": "/docs/api/items/#login_repaired"
        }}><inlineCode parentName="a">{`LOGIN_REPAIRED`}</inlineCode></a>{` webhook. Upon receiving this webhook, you can dismiss any messaging you are presenting to the user telling them to fix their Item via update mode.`}</p>
    </Callout>
    <h5 {...{
      "id": "requesting-additional-products-accounts-permissions-or-use-cases"
    }}>{`Requesting additional products, accounts, permissions, or use cases`}</h5>
    <p>{`You can use update mode to request your users to share new accounts with you. For instructions, see `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/#using-update-mode-to-request-new-accounts"
      }}>{`Using update mode to request new accounts`}</a>{`. Receiving a `}<a parentName="p" {...{
        "href": "/docs/api/items/#new_accounts_available"
      }}><inlineCode parentName="a">{`NEW_ACCOUNTS_AVAILABLE`}</inlineCode></a>{` webhook indicates that Plaid has detected new accounts that you may want to ask your users to share. `}</p>
    <p>{`Update mode is required when adding Assets, Statements, Income, or Plaid Check Consumer Report to an existing Item. For instructions, see `}<a parentName="p" {...{
        "href": "#adding-credit-products"
      }}>{`Adding credit products`}</a>{`.`}</p>
    <p>{`For Items enrolled in `}<a parentName="p" {...{
        "href": "/docs/link/data-transparency-messaging-migration-guide"
      }}>{`Data Transparency Messaging`}</a>{` or Items at Robinhood (`}<inlineCode parentName="p">{`ins_54`}</inlineCode>{`), update mode is required to add any additional product consent. The same flow is required to add Signal Transaction Scores to an existing Item even if the Item is not enrolled in Data Transparency Messaging; it is not required to evaluate a balance with a Balance-only ruleset. For instructions on both, see `}<a parentName="p" {...{
        "href": "#requesting-additional-consented-products"
      }}>{`Requesting additional consented products`}</a>{`. To add use case consent on a Data Transparency Messaging Item, see `}<a parentName="p" {...{
        "href": "#requesting-additional-use-cases"
      }}>{`Requesting additional use cases`}</a>{`.`}</p>
    <p>{`Update mode can optionally be used for resolving errors when adding Auth or Identity; for more details, see `}<a parentName="p" {...{
        "href": "#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
      }}>{`Resolving `}<inlineCode parentName="a">{`ACCESS_NOT_GRANTED`}</inlineCode>{` or `}<inlineCode parentName="a">{`NO_AUTH_ACCOUNTS`}</inlineCode>{` errors via product validations`}</a>{`. `}</p>
    <p>{`Update mode can be used to request OAuth permissions: triggering update mode for an OAuth institution will cause the user to re-enter the OAuth flow, where they can then grant any required OAuth permissions they failed to grant originally, or restore OAuth permissions they may have revoked. The update mode flow for OAuth institutions will also contain guidance recommending which permissions the user should grant: for more details, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#scoped-consent"
      }}>{`OAuth documentation`}</a>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`When an Item is sent through update mode, users can also choose to revoke access they had previously granted. If you lose access to necessary accounts or OAuth permissions after the user completes the update mode flow, you may need to send the user through update mode again. For more details, see `}<a parentName="p" {...{
          "href": "/docs/link/oauth/#managing-consent-revocation"
        }}>{`Managing consent revocation`}</a>{`. To prevent users from revoking access to the Auth or Identity products, you can also use `}<a parentName="p" {...{
          "href": "#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
        }}>{`update mode with Product Validations`}</a>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "refreshing-item-expiration-dates"
    }}>{`Refreshing Item expiration dates`}</h5>
    <p><a parentName="p" {...{
        "href": "/docs/link/oauth/#refreshing-item-consent"
      }}>{`Certain institutions`}</a>{` enforce expiration dates on Item consent. You can find an Item's consent expiration date by calling `}<inlineCode parentName="p">{`/item/get`}</inlineCode>{` and checking the `}<inlineCode parentName="p">{`consent_expiration_time`}</inlineCode>{` value in the response. `}</p>
    <p>{`If an Item has an expiration date, update mode can be used to renew consent on that Item. Whenever an Item is successfully sent through update mode, the Item consent expiration date will be updated as though the Item were newly created.`}</p>
    <p>{`Seven days before an Item's consent expires, you will receive a `}<a parentName="p" {...{
        "href": "#resolving-item_login_required-pending_expiration-or-pending_disconnect"
      }}><inlineCode parentName="a">{`PENDING_DISCONNECT`}</inlineCode>{` (US/CA) or `}<inlineCode parentName="a">{`PENDING_EXPIRATION`}</inlineCode>{` (UK/EU) webhook`}</a>{` to notify you to launch update mode. However, you do not need to wait for this webhook in order to refresh the Item. `}</p>
    <p>{`To test this flow in Sandbox, create an Item at the First Platypus Bank - OAuth test institution (`}<inlineCode parentName="p">{`ins_129644`}</inlineCode>{`), the only Sandbox institution that populates `}<inlineCode parentName="p">{`consent_expiration_time`}</inlineCode>{`. Items created at this institution are assigned an expiration date 30 days in the future.`}</p>
    <h4 {...{
      "id": "using-update-mode"
    }}>{`Using update mode`}</h4>
    <h5 {...{
      "id": "with-an-access-token"
    }}>{`With an access token`}</h5>
    <p>{`To use update mode for an Item, initialize Link with a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` configured with the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for the Item that you wish to update.`}</p>
    <h5 {...{
      "id": "with-a-user-id-or-user-token"
    }}>{`With a user id or user token`}</h5>
    <p>{`To use update mode for a user, initialize Link with a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for the user whose items you wish to update. Configure the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` with the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` if your integration uses the new User APIs, or the `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` if it is a legacy Plaid Check or Income integration, and set the `}<a parentName="p" {...{
        "href": "/docs/api/link/#link-token-create-request-update-user"
      }}><inlineCode parentName="a">{`update.user`}</inlineCode></a>{` field to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. This is the only way to use update mode if you are using `}<a parentName="p" {...{
        "href": "/docs/check"
      }}>{`Plaid Check`}</a>{` products. In Link, the user will be prompted to repair the broken Item. If there are multiple Items in need of repair, the user will be prompted to fix the most recently broken Item. To repair all items associated with a user, you will need to send the user through Link once for each broken Item (you can reuse the same Link token). You can retrieve the status of all Items associated with a user by calling the `}<inlineCode parentName="p">{`/user/items/get`}</inlineCode>{` endpoint.`}</p>
    <h5 {...{
      "id": "with-a-transfer-authorization-id"
    }}>{`With a transfer authorization id`}</h5>
    <p>{`To use update mode for a Transfer authorization with `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` as the decision rationale code, initialize Link with a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` configured with `}<inlineCode parentName="p">{`transfer.authorization_id`}</inlineCode>{` set for the authorization that you wish to update; no access token is required. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#repairing-items-in-item_login_required-state"
      }}>{`Transfer documentation`}</a>{`.`}</p>
    <h5 {...{
      "id": "sample-code"
    }}>{`Sample code`}</h5>
    <Callout mdxType="Callout">
      <p>{`No products should be added to the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array and no product-specific request parameters should be specified when creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for update mode, unless you are using update mode to `}<a parentName="p" {...{
          "href": "#adding-credit-products"
        }}>{`add Assets, Statements, Income, or Plaid Check Consumer Report`}</a>{`, or if you are using Auth and/or Identity and wish to use the beta `}<a parentName="p" {...{
          "href": "#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
        }}>{`update mode with Product Validations flow`}</a>{`. If you are using update mode to `}<a parentName="p" {...{
          "href": "#requesting-additional-consented-products"
        }}>{`add consented products`}</a>{`, the products must be added to the `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` array instead.`}</p>
    </Callout>
    <p>{`You can obtain a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint.`}</p>
    <p>{`If your integration uses redirect URIs normally, create the Link token with a redirect URI, as described in `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#configure-your-link-token-with-your-redirect-uri"
      }}>{`Configure your Link token with your redirect URI`}</a>{`.`}</p>
    <MDXCodeBlock title='/link/token/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`@app.route("/create_link_token", methods=['POST'])
def create_link_token():
    # Create a one-time use link_token for the Item.
    # This link_token can be used to initialize Link
    # in update mode for the user.

    # Create a link_token for the given user
    request = LinkTokenCreateRequest(
            client_name="My App",
            country_codes=[CountryCode('US')],
            language='en',
            access_token = 'ENTER_YOUR_ACCESS_TOKEN_HERE',
            redirect_uri = "https://www.sample.com/redirect.html",
            webhook='https://webhook.sample.com',
            user=LinkTokenCreateRequestUser(
                client_user_id=client_user_id
            )
        )
    response = client.link_token_create(request)
    return jsonify(response)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// Create a one-time use link_token for the Item.
// This link_token can be used to initialize Link
// in update mode for the user
const configs = {
  user: {
    client_user_id: 'UNIQUE_USER_ID',
  },
  client_name: 'Your App Name Here',
  country_codes: [CountryCode.Us],
  language: 'en',
  webhook: 'https://webhook.sample.com',
  redirect_uri: "https://www.sample.com/redirect.html",
  access_token: 'ENTER_YOUR_ACCESS_TOKEN_HERE',
};
app.post('/create_link_token', async (request, response, next) => {
  const linkTokenResponse = await client.linkTokenCreate(configs);

  // Use the link_token to initialize Link
  response.json({ link_token: linkTokenResponse.data.link_token });
});
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Create a one-time use link_token for the Item.
# This link_token can be used to initialize Link
# in update mode for the user.
post '/create_link_token' do

  link_token_create_request = Plaid::LinkTokenCreateRequest.new(
    {
      :user => { :client_user_id => 'user-id' },
      :client_name => 'My App',
      :access_token => 'ENTER_YOUR_ACCESS_TOKEN_HERE',
      :country_codes => ['US'],
      :language => "en",
      :redirect_uri => "https://www.sample.com/redirect.html",
      :webhook => 'https://webhook.sample.com'
    }
  )
  response = client.link_token_create(link_token_create_request)
  content_type :json
  response.to_json
end
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "My App",
  "user": { "client_user_id": "\${UNIQUE_USER_ID}" },
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://webhook.sample.com",
  "redirect_uri": "https://www.sample.com/redirect.html",
  "access_token": "\${ACCESS_TOKEN}"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`ctx := context.Background()

user := plaid.LinkTokenCreateRequestUser{
  ClientUserId: "USER_ID_FROM_YOUR_DB",
}
request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUser(user)
request.SetWebhook("https://webhook-uri.com")
request.SetRedirectUri("https://www.sample.com/redirect.html")
request.SetAccessToken("ENTER_YOUR_ACCESS_TOKEN_HERE")
resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).LinkTokenCreateRequest(*request).Execute()

// Handle err
// Use the generated link_token to initialize Plaid Link
// in update mode for a user's Item so that they can provide
// updated credentials or MFA information
linkToken := resp.GetLinkToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`ctx := context.Background()

user := plaid.LinkTokenCreateRequestUser{ClientUserID: "USER_ID_FROM_YOUR_DB"}
request := &plaid.LinkTokenCreateRequest{
    ClientName:   "Plaid Test",
    Language:     "en",
    CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
}
request.User = &user
request.Webhook = plaid.String("https://webhook-uri.com")
request.RedirectURI = plaid.String("https://www.sample.com/redirect.html")
request.AccessToken = plaid.String("ENTER_YOUR_ACCESS_TOKEN_HERE")
resp, err := client.Link.CreateToken(ctx, request)

// Handle err
// Use the generated link_token to initialize Plaid Link
// in update mode for a user's Item so that they can provide
// updated credentials or MFA information
linkToken := resp.GetLinkToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`// Simplified pseudo-code for obtaining a user_id

User userFromDB = db.find(...);
String clientUserId = userFromDB.id;
LinkTokenCreateRequestUser user = new LinkTokenCreateRequestUser()
  .clientUserId(clientUserId);

// Create a link_token for the given user
LinkTokenCreateRequest request = new LinkTokenCreateRequest()
  .user(user)
  .clientName("My App")
  .accessToken("ENTER_ACCESS_TOKEN_HERE")
  .countryCodes(Arrays.asList(CountryCode.US))
  .language("en")
  .redirectUri("https://www.sample.com/redirect.html")
  .webhook("https://sample.webhook.com");

Response<LinkTokenCreateResponse> response = plaidClient
  .linkTokenCreate(request)
  .execute();

if (response.isSuccessful()) {
  // Use the generated link_token to initialize Plaid Link
  // in update mode for a user's Item so that they can provide
  // updated credentials or MFA information
  String linkToken = response.body().getLinkToken();
}
`}</code></pre>
    </MDXCodeBlock>
  
    <p>{`Link auto-detects the appropriate institution and handles the credential and multi-factor authentication process, if needed.`}</p>
    <p>{`An Item's `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` does not change when using Link in update mode, so there is no need to repeat the exchange token process. Likewise, a User's `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` or `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` does not change when using Link in update mode, so there is no need to create a new `}<inlineCode parentName="p">{`user_token`}</inlineCode>{` or `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #Initializing Link in update mode",
        "switcher": "false",
        "#Initializing": true,
        "Link": true,
        "in": true,
        "update": true,
        "mode": true
      }}>{`// Initialize Link with the token parameter
// set to the generated link_token for the Item
const linkHandler = Plaid.create({
  token: 'GENERATED_LINK_TOKEN',
  onSuccess: (public_token, metadata) => {
    // You do not need to repeat the /item/public_token/exchange
    // process when a user uses Link in update mode.
    // The Item's access_token has not changed.
  },
  onExit: (err, metadata) => {
    // The user exited the Link flow.
    if (err != null) {
      // The user encountered a Plaid API error prior
      // to exiting.
    }
    // metadata contains the most recent API request ID and the
    // Link session ID. Storing this information is helpful
    // for support.
  },
});
`}</code></pre>
    <p>{`When an Item is restored from the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state via update mode, if it has been initialized with a product that sends Item webhooks (such as Transactions or Investments), the next webhook fired for the Item will include data for all missed information back to the last time Plaid made a successful connection to the Item. `}</p>
    <p>{`If the Item has been initialized with Transactions, and was in an error state for over 24 hours, Plaid will check for new transactions immediately after update mode is complete.`}</p>
    <h5 {...{
      "id": "using-update-mode-to-request-new-accounts"
    }}>{`Using update mode to request new accounts`}</h5>
    <Callout globe mdxType="Callout">
      <p>{`The use of update mode to select new accounts is not available for end user financial institution accounts in the UK or EU. In those regions, delete the old Item using `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{` and create a new one instead.`}</p>
    </Callout>
    <p>{`You can allow end users to add new accounts to an Item by enabling Account Select when initializing Link in update mode. To do so, first initialize Link for update mode by creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint, passing in the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and other parameters as you normally would in update mode. `}</p>
    <p>{`In addition, make sure you specify the following:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`update.account_selection_enabled`}</inlineCode>{` flag set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) A `}<inlineCode parentName="p">{`link_customization_name`}</inlineCode>{`. The settings on this customization will determine which selection option for the `}<a parentName="p" {...{
            "href": "/docs/link/customization/#account-select"
          }}>{`Account Select pane`}</a>{` is shown in the update mode flow.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) Any `}<inlineCode parentName="p">{`account_filters`}</inlineCode>{` to specify `}<a parentName="p" {...{
            "href": "/docs/api/link/#link-token-create-request-account-filters"
          }}>{`account filtering`}</a>{`. Note that this field can only be set for update mode if `}<inlineCode parentName="p">{`update.account_selection_enabled`}</inlineCode>{` is set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
      </li>
    </ol>
    <p>{`Once your user has updated their account selection, all selected accounts will be shared in the `}<a parentName="p" {...{
        "href": "/docs/link/web/#link-web-onsuccess-metadata-accounts"
      }}><inlineCode parentName="a">{`accounts`}</inlineCode></a>{` field in the `}<a parentName="p" {...{
        "href": "/docs/link/web/#onsuccess"
      }}><inlineCode parentName="a">{`onSuccess()`}</inlineCode></a>{` callback from Link. Any de-selected accounts will no longer be shared with you. You will only be able to receive data for the accounts the user selects in update mode going forward.`}</p>
    <p>{`Adding an account through update mode will cause Plaid to fetch data for the newly added account. However, `}<a parentName="p" {...{
        "href": "/docs/transactions/#recurring-transactions"
      }}>{`recurring transactions`}</a>{` streams will not be calculated for the new account until either the next periodic transactions update for the Item occurs or the `}<inlineCode parentName="p">{`/transactions/refresh`}</inlineCode>{` endpoint is called. `}</p>
    <p>{`This update mode flow can also be used to remove accounts from an Item. We recommend that you remove any data associated with accounts that your user has de-selected. Note that Chase is an exception to the ability to remove accounts via update mode; to remove access to a specific account on a Chase Item, the end user must do so through the online Chase Security Center.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Example /link/token/create call to request new accounts in update mode",
        "switcher": "false",
        "#Example": true,
        "/link/token/create": true,
        "call": true,
        "to": true,
        "request": true,
        "new": true,
        "accounts": true,
        "in": true,
        "update": true,
        "mode": true
      }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "My App",
  "user": { "client_user_id": "\${UNIQUE_USER_ID}" },
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://webhook.sample.com",
  "access_token": "\${ACCESS_TOKEN}",
  "link_customization_name": "account_selection_v2_customization",
  "redirect_uri": "https://www.sample.com/redirect.html",
  "update": { "account_selection_enabled": true }
}'
`}</code></pre>
    <p>{`When using the Assets product specifically, if a user selects additional accounts during update mode but does not successfully complete the Link flow, Assets authorization will be revoked from the Item. If this occurs, have the user go through a new Link flow in order to generate an Asset Report, or, if you have Data Transparency Messaging enabled, use update mode to re-authorize the Item for assets, as described in the next section.`}</p>
    <p>{`If you are using update mode to add a debitable checking or savings account in response to a `}<inlineCode parentName="p">{`NO_AUTH_ACCOUNTS`}</inlineCode>{` error, see `}<a parentName="p" {...{
        "href": "#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
      }}>{`Resolving `}<inlineCode parentName="a">{`ACCESS_NOT_GRANTED`}</inlineCode>{` or `}<inlineCode parentName="a">{`NO_AUTH_ACCOUNTS`}</inlineCode>{` errors via product validations`}</a>{` for a better method to resolve this error.`}</p>
    <h5 {...{
      "id": "resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
    }}>{`Resolving ACCESS_NOT_GRANTED or NO_AUTH_ACCOUNTS errors via Product Validations`}</h5>
    <Callout mdxType="Callout">
      <p>{`To request access to update mode with Product Validations, contact support or your Plaid account manager. If you attempt to use this flow before being granted access, the update mode flow will work, but the product validations will not be applied.`}</p>
    </Callout>
    <p>{`The Auth and Identity products can be added to an Item post-Link, by calling an Auth or Identity related endpoint, rather than including `}<inlineCode parentName="p">{`auth`}</inlineCode>{` or `}<inlineCode parentName="p">{`identity`}</inlineCode>{` in the products array. However, if the user did not share the necessary permissions or accounts to support these products, the Item will return an `}<inlineCode parentName="p">{`ACCESS_NOT_GRANTED`}</inlineCode>{` or `}<inlineCode parentName="p">{`NO_AUTH_ACCOUNTS`}</inlineCode>{` error. Update mode with Product Validations (UMPV) applies product-specific validations to the selections a user makes in the update mode flow, resulting in higher conversion than resolving these errors via regular update mode.`}</p>
    <p>{`The process to use UMPV is the same as described in `}<a parentName="p" {...{
        "href": "#using-update-mode"
      }}>{`Using update mode`}</a>{`, except that you will also include the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array in the request with the value `}<inlineCode parentName="p">{`auth`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`identity`}</inlineCode>{` for the product(s) you wish to validate. You must use the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array with UMPV; including `}<inlineCode parentName="p">{`auth`}</inlineCode>{` or `}<inlineCode parentName="p">{`identity`}</inlineCode>{` in other fields such as `}<inlineCode parentName="p">{`required_if_supported_products`}</inlineCode>{` or `}<inlineCode parentName="p">{`optional_products`}</inlineCode>{` will not activate UMPV.`}</p>
    <p>{`UMPV will enforce the same level of product validation as is normally used on an initial Link attempt: the user will be instructed on which permissions to grant, and if they do not make these selections, they will be prompted to go back through the flow. In the case of `}<inlineCode parentName="p">{`NO_AUTH_ACCOUNTS`}</inlineCode>{`, the account selection flow will also be automatically enabled if necessary.`}</p>
    <p>{`UMPV can also be used preventatively, to prevent users in the update mode flow from accidentally removing permissions they have already granted. Applying UMPV to any update mode session for an Auth- or Identity-enabled Item will prompt users to fix their selections if they remove the accounts or permissions required for these products.`}</p>
    <p>{`UMPV can only be used for `}<inlineCode parentName="p">{`auth`}</inlineCode>{` or `}<inlineCode parentName="p">{`identity`}</inlineCode>{`. It is also not compatible with `}<a parentName="p" {...{
        "href": "#adding-credit-products"
      }}>{`Adding credit products`}</a>{`; the two cannot be used in a single update mode flow, although they can be used on the same Item, via separate update mode sessions.`}</p>
    <h5 {...{
      "id": "adding-credit-products"
    }}>{`Adding credit products`}</h5>
    <p>{`Credit products (Assets, Statements, Income, and Plaid Check Consumer Report) have unique consent flows in Link. To add one of these products to an Item that did not previously have it enabled, you will need to send the user through update mode so that they can go through the product-specific consent flow before the product is added to the Item.`}</p>
    <p>{`The process to do this is the same as described in `}<a parentName="p" {...{
        "href": "#using-update-mode"
      }}>{`Using update mode`}</a>{`, except that in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request, you will also include the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array in the request with the value for the product you wish to add, such as `}<inlineCode parentName="p">{`assets`}</inlineCode>{`, `}<inlineCode parentName="p">{`statements`}</inlineCode>{`, `}<inlineCode parentName="p">{`income_verification`}</inlineCode>{`, `}<inlineCode parentName="p">{`cra_base_report`}</inlineCode>{`, etc. You will also need to add any required product-specific fields -- for example, if adding `}<inlineCode parentName="p">{`statements`}</inlineCode>{`, you will need to add the `}<inlineCode parentName="p">{`statements`}</inlineCode>{` configuration object. For examples and details, see the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` documentation.`}</p>
    <p>{`If the user connected their account less than two years ago, they can bypass the Link credentials pane and complete just the consent panes. Otherwise, they will be prompted to complete the full flow. `}</p>
    <h4 {...{
      "id": "requesting-additional-consented-products"
    }}>{`Requesting additional consented products`}</h4>
    <p>{`To collect a user's consent for additional products on an existing Item via the `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` field of `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, send the user through update mode. This flow is required in two cases:`}</p>
    <ul>
      <li parentName="ul">{`For Items enrolled in `}<a parentName="li" {...{
          "href": "/docs/link/data-transparency-messaging-migration-guide"
        }}>{`Data Transparency Messaging (DTM)`}</a>{` or Items at Robinhood (`}<inlineCode parentName="li">{`ins_54`}</inlineCode>{`), to access any product the user did not already consent to.`}</li>
      <li parentName="ul">{`To use `}<a parentName="li" {...{
          "href": "/docs/signal"
        }}>{`Signal Transaction Scores`}</a>{` on any Item, `}<strong parentName="li">{`even if the Item is not enrolled in DTM`}</strong>{`. Without this consent, calling `}<inlineCode parentName="li">{`/signal/evaluate`}</inlineCode>{` with a ruleset that uses Signal Transaction Scores returns the `}<a parentName="li" {...{
          "href": "/docs/errors/invalid-input/#additional_consent_required"
        }}><inlineCode parentName="a">{`ADDITIONAL_CONSENT_REQUIRED`}</inlineCode></a>{` error. Evaluating a balance with a Balance-only ruleset does not require this consent.`}</li>
    </ul>
    <p>{`This flow is not available for Items created via Same-Day Micro-deposits, Instant Micro-deposits, or Database Auth; for those Items, request consent for all products you might need when creating the Item.`}</p>
    <p>{`To do so, initialize Link for update mode by creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint and passing in the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. For OAuth institutions, ensure you configure the Link token with a redirect URI, as described in `}<a parentName="p" {...{
        "href": "/docs/link/oauth/#configure-your-link-token-with-your-redirect-uri"
      }}>{`Configure your Link token with your redirect URI`}</a>{`.`}</p>
    <p>{`In addition, make sure you specify the following when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`:`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` field should be set to include `}<strong parentName="li">{`any new products you want to gather consent for.`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`For example, if Link was initialized with just Transactions and you want to add Signal, you would pass in `}<inlineCode parentName="li">{`signal`}</inlineCode>{` to the `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{` field.`}</li>
          <li parentName="ul">{`To see the currently authorized and consented products on an Item, use the `}<inlineCode parentName="li">{`/item/get`}</inlineCode>{` endpoint.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`For Items enrolled in DTM, the `}<inlineCode parentName="li">{`link_customization_name`}</inlineCode>{` should be set to a customization with Data Transparency Messaging enabled. The use case string should also be broadened to include your reasons for accessing the new data. If the use case is not customized, the default use case will be present on the Data Transparency Messaging pane.`}</li>
    </ul>
    <p>{`If the upgrade was successful, you will receive the `}<inlineCode parentName="p">{`onSuccess()`}</inlineCode>{` callback and you will have access to the API endpoints for all of the products you passed into Link update mode. The new products will only be billed once the related API endpoints are called, even if they would otherwise be billed upon Item initialization (e.g., Transactions).`}</p>
    <Callout mdxType="Callout">
      <p>{`When using update mode to add product consent, you must use the `}<inlineCode parentName="p">{`additional_consented_products`}</inlineCode>{` parameter, not the `}<inlineCode parentName="p">{`products`}</inlineCode>{` parameter, unless you are adding `}<a parentName="p" {...{
          "href": "#adding-credit-products"
        }}>{`Assets, Statements, Income, or Plaid Check Consumer Report`}</a>{` or using `}<a parentName="p" {...{
          "href": "#resolving-access_not_granted-or-no_auth_accounts-errors-via-product-validations"
        }}>{`Product Validations (beta)`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "data-transparency-messaging"
    }}>{`Data Transparency Messaging`}</h4>
    <p>{`For Items that are enabled for `}<a parentName="p" {...{
        "href": "/docs/link/data-transparency-messaging-migration-guide"
      }}>{`Data Transparency Messaging (DTM)`}</a>{`, update mode is required to request additional consented products or additional consented use cases. To add product consent, see `}<a parentName="p" {...{
        "href": "#requesting-additional-consented-products"
      }}>{`Requesting additional consented products`}</a>{`.`}</p>
    <h5 {...{
      "id": "requesting-additional-use-cases"
    }}>{`Requesting additional use cases`}</h5>
    <p>{`For Items with DTM enabled, your user will be prompted to consent to their data being used for specific use cases, not just specific products. If you need to obtain consent for additional use cases, you can update your Link customization to add the use cases, then send your user through update mode. `}</p>
    <h4 {...{
      "id": "update-mode-and-layer"
    }}>{`Update mode and Layer`}</h4>
    <p>{`During the `}<a parentName="p" {...{
        "href": "/docs/layer"
      }}>{`Layer`}</a>{` flow, if the end user has an Item in an unhealthy state that requires update mode, this Item will still be surfaced in Link. If the user chooses to share this Item via Layer, they will automatically be sent through update mode as part of the Layer flow.`}</p>
    <h4 {...{
      "id": "update-mode-at-chase-with-auth"
    }}>{`Update mode at Chase with Auth`}</h4>
    <p>{`When using update mode with Auth for Items at Chase, you need to refresh the Item's account and routing number after completing update mode only if the Item's TAN had already expired. In that case, the old TAN is not re-activated; a new one is minted, and you must call `}<inlineCode parentName="p">{`/auth/get`}</inlineCode>{` (or `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{` or `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{`) to retrieve it. If you complete update mode before the Item's consent expires, the existing TAN remains valid and no refresh is needed.`}</p>
    <p>{`The same applies when using update mode to restore an Item after the end user has revoked access, which also invalidates the TAN. In that case, always fetch the account and routing number again after completing update mode. For more details, see `}<a parentName="p" {...{
        "href": "/docs/auth/#tan-expiration"
      }}>{`TAN expiration`}</a>{` and `}<a parentName="p" {...{
        "href": "/docs/auth/#tokenized-account-numbers"
      }}>{`Tokenized account numbers`}</a>{`. This only impacts Chase Items used with Auth or Transfer; you do not need to do this for Items at other institutions or when using other products.`}</p>
    <h4 {...{
      "id": "processor-tokens"
    }}>{`Processor tokens`}</h4>
    <p>{`When using update mode on an Item associated with a processor token, all existing processor tokens will automatically receive the updates, since they are linked to the access token, which does not change during update mode. However, if the user changes the linked account (e.g. by selecting a different account via update mode), you will need to generate a new processor token by calling `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "testing-update-mode"
    }}>{`Testing update mode`}</h4>
    <p>{`Update mode can be tested in the Sandbox using the `}<inlineCode parentName="p">{`/sandbox/item/reset_login`}</inlineCode>{` endpoint, which will force a given Item into an `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state. For user-based products, like Consumer Report or Bank Income,  `}<inlineCode parentName="p">{`/sandbox/user/reset_login`}</inlineCode>{` can be used to force all Items associated with a given user into an `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` state.`}</p>
    <h4 {...{
      "id": "example-react-code-in-plaid-pattern"
    }}>{`Example React code in Plaid Pattern`}</h4>
    <p>{`For a real-life example that illustrates the handling of update mode, see `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/server/routes/linkTokens.js#L30-L35"
      }}>{`linkTokens.js`}</a>{` and `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/client/src/components/LaunchLink.tsx#L46-L50"
      }}>{`LaunchLink.tsx`}</a>{`. These files contain the Link update mode code for the React-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern"
      }}>{`Plaid Pattern`}</a>{` sample app.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "when-to-use-update-mode",
        level: 4,
        title: "When to use update mode",
      },
{
        id: "using-update-mode",
        level: 4,
        title: "Using update mode",
      },
{
        id: "requesting-additional-consented-products",
        level: 4,
        title: "Requesting additional consented products",
      },
{
        id: "data-transparency-messaging",
        level: 4,
        title: "Data Transparency Messaging",
      },
{
        id: "update-mode-and-layer",
        level: 4,
        title: "Update mode and Layer",
      },
{
        id: "update-mode-at-chase-with-auth",
        level: 4,
        title: "Update mode at Chase with Auth",
      },
{
        id: "processor-tokens",
        level: 4,
        title: "Processor tokens",
      },
{
        id: "testing-update-mode",
        level: 4,
        title: "Testing update mode",
      },
{
        id: "example-react-code-in-plaid-pattern",
        level: 4,
        title: "Example React code in Plaid Pattern",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
